import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Button, Stack } from "@mui/material";

import { FormSelectField, FormTextField } from "@covertree/ui";
import { UnitLevelUnderwritingAnswers } from "@covertree/types";
import { TypeOfFuelValue } from "@covertree/new-business-graphql";

import { QuestionModalProps } from "../underwritingQuestions";
import {
  mapQuestionOptionsToFormOptions,
  mapTypeOfFuelValue,
} from "../../../mappings/underwritingQuestionsMaps";
import { UnderwritingQuestionsForm } from "../../../validatorsAndTypes/underwritingQuestions";

export function SupplementalHeatingForm({
  questions,
  closeModal,
}: QuestionModalProps) {
  const { formState, watch, control, setValue } =
    useFormContext<UnderwritingQuestionsForm>();

  const typeOfFuel = watch("typeOfFuel");
  const sourceOfHeatOptions = useMemo(
    () =>
      mapQuestionOptionsToFormOptions(
        questions?.[UnitLevelUnderwritingAnswers.SourceOfHeatInstallation]
          .options
      ),
    [questions]
  );
  const typeOfFuelOptions = useMemo(
    () =>
      questions?.[UnitLevelUnderwritingAnswers.TypeOfFuel].options?.map(
        (value) => {
          return {
            label: value,
            value: mapTypeOfFuelValue(value),
          };
        }
      ) ?? [],
    [questions]
  );

  useEffect(() => {
    if (typeOfFuel !== TypeOfFuelValue.NotListed) {
      setValue("pleaseDescribe", null, { shouldValidate: true });
    }
  }, [typeOfFuel]);

  return (
    <Stack spacing={4}>
      <Box>
        <FormSelectField
          control={control}
          name="sourceOfHeatInstallation"
          id="supplemental-heating-installation"
          dataTestId="supplemental-heating-installation"
          options={sourceOfHeatOptions}
          label="Was the device professionally installed?"
        />
      </Box>
      <Box>
        <FormSelectField
          control={control}
          name="typeOfFuel"
          id="supplemental-heating-type-of-fuel"
          dataTestId="supplemental-heating-type-of-fuel"
          options={typeOfFuelOptions}
          label="What kind of fuel does the device use?"
        />
      </Box>
      {typeOfFuel === TypeOfFuelValue.NotListed && (
        <Box>
          <FormTextField
            multiline
            rows={4}
            control={control}
            name="pleaseDescribe"
            id="supplemental-heating-fuel-description"
            dataTestId="supplemental-heating-fuel-description"
            label="Description"
          />
        </Box>
      )}
      <Box>
        <Button
          type="button"
          onClick={closeModal}
          disabled={!formState.isValid}>
          Save
        </Button>
      </Box>
    </Stack>
  );
}
